import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Button } from "swing-components";
import { phoneNumberFormatter } from "swing-utils";

import { ErrorMessage, PhoneInput, Toast } from "~components";
import type { PhoneAnswerType } from "~onboarding/utils";
import {
  PhoneVerificationError,
  PhoneVerificationPayload,
  PhoneVerificationSuccess,
  POST,
  translate,
  useLocalStorage,
} from "~utils";

type PhoneAnswerProps = Omit<PhoneAnswerType, "type"> & {
  /**
   * The phone number stored in the onboarding attribute.
   */
  value?: string;
  /**
   * When entering a phone number, pass the `value` to the parent.
   */
  onChange: (value?: string) => void;
  /**
   * Disables the editability of the answer.
   */
  isReadOnly?: boolean;
};

const PHONE_NUMBER_STORAGE_KEY = "onboarding-phone-number";

export function PhoneAnswer(props: PhoneAnswerProps) {
  const { value, onChange, isReadOnly } = props;
  const [savedPhoneNumber] = useState<string>(value || "");
  const [phoneNumber, setPhoneNumber] = useState<string>(value || "");
  const [, setStoredPhoneNumber] = useLocalStorage(PHONE_NUMBER_STORAGE_KEY, phoneNumber);
  const [isLocalError, setIsLocalError] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const {
    mutate: requestCode,
    isPending,
    error,
  } = useMutation<
    PhoneVerificationSuccess | PhoneVerificationError,
    AxiosError<{ detail?: { exception?: string } }>,
    PhoneVerificationPayload
  >({
    mutationFn: (payload) => POST("/api/sub/phone-verification", payload),
    onSuccess: () => {
      onChange(phoneNumber);
      setShowToast(true);
    },
    onError: () => setIsLocalError(true),
  });
  const isPhoneNumberValid = phoneNumber.length === 10;
  const isRequestCodeDisabled =
    isReadOnly || isPending || !isPhoneNumberValid || value === phoneNumber;
  const isDuplicatePhoneNumberError =
    error?.response?.data?.detail?.exception === "duplicate-phone-number-found";

  function handleRequestCode() {
    setIsLocalError(false);
    if (isPhoneNumberValid) {
      requestCode({ phoneNumber });
    }
  }

  useEffect(() => {
    if (!isPhoneNumberValid) {
      setIsLocalError(false);
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (phoneNumber !== "") {
      setStoredPhoneNumber(phoneNumber);
    }
  }, [phoneNumber]);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <PhoneInput
        phoneNumber={phoneNumber}
        onChange={(v) => {
          setPhoneNumber(v);
          onChange(v !== "" && v === savedPhoneNumber ? v : undefined);
        }}
        isDisabled={isReadOnly || false}
        error={
          isLocalError ? (
            <ErrorMessage>
              <span>
                {isDuplicatePhoneNumberError
                  ? translate("PROFILE_PHONE_NUMBER_DUPLICATE_ERROR_MESSAGE")
                  : translate("PROFILE_PHONE_NUMBER_INVALID_ERROR_MESSAGE")}{" "}
                <a target="_blank" rel="noreferrer" href="mailto:support@swingeducation.com">
                  Contact support
                </a>
                .
              </span>
            </ErrorMessage>
          ) : null
        }
      />
      <Button
        type="primary"
        isFluid
        status={isRequestCodeDisabled ? "disabled" : isPending ? "loading" : "enabled"}
        onClick={handleRequestCode}
      >
        {translate("PROFILE_PHONE_NUMBER_REQUEST_CODE_BUTTON_LABEL")}
      </Button>
      <Toast
        isVisible={showToast}
        type="positive"
        onDismiss={() => setShowToast(false)}
        text={`${translate("PHONE_VERIFICATION_ENTER_CODE_TOAST_MSG")} to ${phoneNumberFormatter({ phoneNumber }).value}`}
      />
    </div>
  );
}
